import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { currentMembership, useEvents, useHousehold, type Person } from '@/lib/queries';
import { dateTime, gradeLabel } from '@/lib/format';
import { ErrorNote, Spinner } from '@/components/Layout';

const REASON: Record<string, string> = {
  frozen: 'has a paused membership; please contact the Membership Secretary',
  needs_membership: 'needs an EGKA membership that covers the grading date', already_entered: 'is already entered',
  grade_not_allowed: 'can only grade for the next grade or the one after', grading_closed: 'entries have closed', not_your_household: 'is not in your household',
};

/** Enter people in your household for the grading at an event. Their sensei then approves. The fee is paid on the day. */
export function EnterGrading() {
  const { id = '' } = useParams();
  const events = useEvents();
  const household = useHousehold();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const event = events.data?.find(e => e.id === id);
  const [who, setWho] = useState('');
  const [f, setF] = useState({ for_rank: '', sessions_per_week: '', started_goju_ryu_on: '', started_karate_on: '', name_katakana: '', nationality: '', gender: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const onDay = event?.starts_at.slice(0, 10) ?? '';
  const people = household.data?.people ?? [];
  const person: Person | undefined = people.find(p => p.id === who);
  const entered = (p: Person) => p.gradings?.some(g => g.event?.id === id && g.result !== 'withdrawn');
  const member = (p: Person) => { const m = currentMembership(p); return !!m && m.status === 'active' && m.ends_on >= onDay; };
  const options = useMemo(() => { const r = person?.grade_rank ?? 0; return [r + 1, r + 2].filter(x => x <= 20); }, [person]);
  const forRank = Number(f.for_rank || options[0] || 0);
  const isDan = forRank >= 11;

  if (events.isLoading || household.isLoading) return <Spinner />;
  if (!household.data) return <Navigate to="/join" replace />;
  if (!event || !event.grading_offered) return <ErrorNote>There's no grading to enter at this event.</ErrorNote>;

  async function submit() {
    if (!person) return;
    setBusy(true); setError('');
    const { error } = await supabase.rpc('request_grading', { p_event: id, p_person: person.id, p_for_rank: forRank, p: {
      sessions_per_week: f.sessions_per_week, started_goju_ryu_on: f.started_goju_ryu_on, started_karate_on: f.started_karate_on,
      name_katakana: f.name_katakana, nationality: f.nationality, gender: f.gender,
    } });
    if (error) {
      const code = Object.keys(REASON).find(k => error.message.includes(k));
      setError(code ? `${person.first_name} ${REASON[code]}.` : 'Something went wrong. Please try again.');
      setBusy(false); return;
    }
    api('/gradings/notify', { method: 'POST', body: {} }).catch(() => {});  // email the sensei now; the 6am job is the fallback
    await qc.invalidateQueries({ queryKey: ['household'] });
    navigate('/home?entered=1');
  }

  const field = (label: string, el: React.ReactNode, hint?: string) => <label className="block"><span className="label">{label}</span>{el}{hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}</label>;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Link to={`/events/${event.slug}`} className="text-sm text-ink-500 hover:underline">← {event.title}</Link>
        <h1 className="mt-2 text-3xl">Enter the grading</h1>
        <p className="mt-1 text-ink-500">{dateTime(event.starts_at)}{event.venue && ` · ${event.venue}`}. Your sensei will be asked to approve the entry. The grading fee is paid on the day.</p>
      </div>

      <fieldset className="card space-y-2">
        <legend className="px-1 font-display text-lg font-bold">Who is grading?</legend>
        {people.map(p => {
          const why = entered(p) ? 'already entered' : p.frozen_at ? 'membership paused' : !member(p) ? 'needs a membership covering the grading date' : null;
          return (
            <label key={p.id} className={`flex items-center gap-3 rounded-sm border px-4 py-3 ${who === p.id ? 'border-ink-900' : 'border-stone-200'} ${why ? 'opacity-60' : 'cursor-pointer'}`}>
              <input type="radio" name="who" disabled={!!why} checked={who === p.id} onChange={() => { setWho(p.id); setF({ ...f, for_rank: '' }); }} className="accent-brand-600" />
              <span className="flex-1"><strong>{p.first_name} {p.last_name}</strong> <span className="text-ink-500">· now {gradeLabel(p.grade_rank)}</span></span>
              {why && <span className="text-sm text-ink-500">{why}{!entered(p) && !p.frozen_at && <> · <Link to="/renew" className="text-brand-700 underline">renew</Link></>}</span>}
            </label>
          );
        })}
      </fieldset>

      {person && (
        <fieldset className="card grid gap-4 sm:grid-cols-2">
          <legend className="px-1 font-display text-lg font-bold">{person.first_name}'s grading</legend>
          {field('Grading for', <select className="input" value={forRank} onChange={e => setF({ ...f, for_rank: e.target.value })}>{options.map(r => <option key={r} value={r}>{gradeLabel(r)}</option>)}</select>)}
          {field('Training sessions a week', <input type="number" min="0" max="14" className="input" value={f.sessions_per_week} onChange={e => setF({ ...f, sessions_per_week: e.target.value })} />)}
          {!person.started_goju_ryu_on && field('Started Goju-Ryu (roughly)', <input type="date" className="input" value={f.started_goju_ryu_on} onChange={e => setF({ ...f, started_goju_ryu_on: e.target.value })} />, "We'll remember this.")}
          {!person.started_karate_on && field('Started karate (any style)', <input type="date" className="input" value={f.started_karate_on} onChange={e => setF({ ...f, started_karate_on: e.target.value })} />, 'Leave blank if the same.')}
          {isDan && !person.name_katakana && field('Name in katakana (for the certificate)', <input className="input" lang="ja" value={f.name_katakana} onChange={e => setF({ ...f, name_katakana: e.target.value })} placeholder="アダム・ウォナコット" />, 'As you want it on your IOGKF certificate. Your sensei can help.')}
          {isDan && !person.nationality && field('Nationality', <input className="input" value={f.nationality} onChange={e => setF({ ...f, nationality: e.target.value })} placeholder="British" />)}
          {isDan && field('Gender (IOGKF form)', <select className="input" value={f.gender} onChange={e => setF({ ...f, gender: e.target.value })}><option value="">Prefer not to say</option><option value="female">Female</option><option value="male">Male</option></select>, 'Only EGKA admins can see this.')}
          {!person.photo_path && <p className="text-sm text-brand-700 sm:col-span-2">You can't grade without a photo. Add one for {person.first_name} on <Link to="/home" className="underline">your household page</Link> before the day.</p>}
        </fieldset>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}
      {person && <button className="btn-primary" disabled={busy || !forRank} onClick={submit}>{busy ? 'Entering…' : `Enter ${person.first_name} for ${gradeLabel(forRank)}`}</button>}
    </div>
  );
}
